import { useCallback, useRef, useState, useEffect } from 'react';
import { BaseURL } from 'config';
import { getAuthToken } from 'Helpers';
export interface SSEEvent {
  type: string;
  data: string;
}

export interface AIChunk {
  text?: string;
  error?: string;
}

// Helper function to extract AI chunk data from SSE event
export const extractAIChunk = (event: SSEEvent): AIChunk | null => {
  try {
    return JSON.parse(event.data) as AIChunk;
  } catch {
    return null;
  }
};


export interface AIStreamingOptions {

  onMessage?: (event: SSEEvent) => void;
  onError?: (error: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  autoReconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export interface AIStreamingState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  lastEvent: SSEEvent | null;
  reconnectAttempts: number;
}

export interface AIStreamingMethods {
  streamPromptResult: (path: string) => Promise<void>;
  connect: () => void;
  disconnect: () => void;
}

export const useAIStreaming = (options: AIStreamingOptions = {}): AIStreamingState & AIStreamingMethods => {
  const {
    onMessage,
    onError,
    onOpen,
    onClose,
    autoReconnect = true,
    reconnectInterval = 3000,
    maxReconnectAttempts = 5,
  } = options;

  const [state, setState] = useState<AIStreamingState>({
    isConnected: false,
    isConnecting: false,
    error: null,
    lastEvent: null,
    reconnectAttempts: 0,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const currentParamsRef = useRef<string | null>(null);


  const processJSONChunk = useCallback((chunk: string, onEvent: (event: SSEEvent) => void) => {
    try {
      // Parse the JSON chunk
      const jsonData = JSON.parse(chunk);

      // Create SSE event with the JSON data
      const sseEvent: SSEEvent = {
        type: 'message',
        data: jsonData.text || '',
      };

      onEvent(sseEvent);
    } catch (error) {
      console.error('Error parsing JSON chunk:', error, 'Chunk:', chunk);
      // If JSON parsing fails, treat as plain text
      const sseEvent: SSEEvent = {
        type: 'message',
        data: chunk,
      };
      onEvent(sseEvent);
    }
  }, []);

  const createStreamConnection = useCallback(async (path: string) => {
    if (!isMountedRef.current) return;

    setState(prev => ({ ...prev, isConnecting: true, error: null }));
    currentParamsRef.current = path;

    // Cancel any existing connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      // Build the full URL with query parameters
      const fullUrl = new URL(path, BaseURL);
      // Get authentication token
      const tokens = getAuthToken();
      const headers: Record<string, string> = {
      };

      if (tokens?.token) {
        headers['Authorization'] = `Bearer ${tokens.token}`;
      }

      // Use fetch directly with the same configuration as the API service
      const response = await fetch(fullUrl.toString(), {
        method: 'GET',
        headers,
        signal,
      });


      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      if (!isMountedRef.current) return;

      setState(prev => ({
        ...prev,
        isConnected: true,
        isConnecting: false,
        error: null,
        reconnectAttempts: 0,
      }));

      onOpen?.();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          if (!isMountedRef.current) break;

          const { done, value } = await reader.read();
          if (done) {
            break;
          }


          buffer += decoder.decode(value, { stream: true });

          // Process JSON chunks (each chunk is a complete JSON object)
          const events = buffer.split('\n\n');
          buffer = events.pop() || ''; // Keep incomplete event in buffer

          for (const eventChunk of events) {
            if (eventChunk.trim()) {
              processJSONChunk(eventChunk, (sseEvent) => {
                if (!isMountedRef.current) return;

                setState(prev => ({ ...prev, lastEvent: sseEvent }));
                onMessage?.(sseEvent);
              });
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      if (isMountedRef.current) {
        setState(prev => ({
          ...prev,
          isConnected: false,
          isConnecting: false,
        }));
        onClose?.();
      }

    } catch (error) {
      if (!isMountedRef.current) return;

      const isAbortError =
        error instanceof DOMException
          ? error.name === "AbortError"
          : error instanceof Error && error.name === "AbortError";

      if (isAbortError) {
        setState(prev => ({
          ...prev,
          isConnected: false,
          isConnecting: false,
          error: null,
        }));
        onClose?.();
        return;
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      setState(prev => ({
        ...prev,
        isConnected: false,
        isConnecting: false,
        error: errorMessage,
      }));

      onError?.(errorMessage);

      // Attempt reconnection if enabled and under max attempts
      if (autoReconnect && state.reconnectAttempts < maxReconnectAttempts && currentParamsRef.current) {
        setState(prev => ({
          ...prev,
          reconnectAttempts: prev.reconnectAttempts + 1
        }));

        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current && currentParamsRef.current) {
            createStreamConnection(currentParamsRef.current);
          }
        }, reconnectInterval);
      }
    }
  }, [
    onOpen,
    onMessage,
    onError,
    onClose,
    autoReconnect,
    reconnectInterval,
    maxReconnectAttempts,
    processJSONChunk,
    state.reconnectAttempts,
  ]);

  const streamPromptResult = useCallback(async (path: string) => {
    await createStreamConnection(path);
  }, [createStreamConnection]);

  const connect = useCallback(() => {
    if (currentParamsRef.current) {
      createStreamConnection(currentParamsRef.current);
    }
  }, [createStreamConnection]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setState(prev => ({
      ...prev,
      isConnected: false,
      isConnecting: false,
    }));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // isMountedRef.current = false;
      disconnect();
    };
  }, [disconnect]);

  return {
    ...state,
    streamPromptResult,
    connect,
    disconnect,
  };
};

export default useAIStreaming;
